import React, { ReactNode, FC } from 'react';

import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import { ThemeProps } from '@lucid/theme';
import { isIE11 } from '@lucid/utils';
import { variant } from 'styled-system';

export type NavbarWrapperVariants = 'default';

interface NavbarWrapperProps {
  /** Controls the HTML tag created for the NavbarWrapper component. */
  as?: 'nav' | 'header' | 'div';
  /** Changes the background color for the NavbarWrapper component. */
  background?: string;
  /** Applys additional styles for the NavbarWrapper component. */
  className?: string;
  /** Checks to show the SkipText component on tab. */
  skipText?: string;
  /** Checks whether the navbar should be sticky. */
  isSticky?: boolean;
  /** Checks whether the sticky navbar is collapsed. */
  isStickyCollapsed?: boolean;
  /** Sets the position of the NavbarWrapper component from the top of the viewport. */
  top?: number;
  /** Sets the zIndex of the NavbarWrapper component. */
  zIndex?: number;
  /** The children of the NavbarWrapper component. */
  children?: ReactNode;
  /** The theme object to apply styles to the NavbarWrapper component. */
  theme?: ThemeProps['theme'];
  /** The available variants for the NavbarWrapper component. */
  variant?: NavbarWrapperVariants;
}

const NavbarWrap = styled.nav<NavbarWrapperProps>(
  ({ theme, isSticky, isStickyCollapsed, background, top, zIndex }) => ({
    label: 'navbarWrapper',
    display: 'block',
    background: background || 'white',
    zIndex,
    top,
    left: 0,
    right: 0,
    position: isSticky ? ['fixed', 'sticky'] : 'relative',
    [`@media (max-width: ${theme.navbar.collapseAt})`]: {
      position: isStickyCollapsed ? ['fixed', 'sticky'] : 'relative'
    }
  }),
  variant({
    scale: 'navbar.wrapper',
    variants: {
      defaultVariant: {}
    }
  })
);

const IE11 = isIE11();

const NavbarWrapper: FC<NavbarWrapperProps> = ({
  as,
  background,
  className,
  isSticky = false,
  isStickyCollapsed = false,
  top = 0,
  zIndex = 1000,
  children,
  variant = 'default',
  ...rest
}) => {
  return (
    <NavbarWrap
      {...rest}
      as={as}
      background={background}
      className={className}
      isSticky={isSticky}
      isStickyCollapsed={isStickyCollapsed}
      top={top}
      variant={variant}
      zIndex={zIndex}
    >
      {isSticky && IE11 && (
        <Global
          styles={{
            body: {
              marginTop: '60px'
            }
          }}
        />
      )}
      {children}
    </NavbarWrap>
  );
};

export default NavbarWrapper;
